/**
 * Active-mode chips.
 *
 * A mode that changes what a stroke does — symmetry now, alpha lock and clip
 * next — is invisible once the control that set it scrolls out of view or lives
 * in another pane. The failure is always the same shape: someone paints, gets a
 * result they did not ask for, and cannot see why.
 *
 * So state gets its own place, in the corner of the surface it affects, and
 * every mode that alters a stroke belongs here. A chip is present only while its
 * mode is on — an empty strip is the resting state, so anything showing is
 * something to know about.
 *
 * Each chip is also its own off switch. If a mode is surprising you, the thing
 * telling you about it should be the thing that stops it.
 */

export interface ModeChip {
  id: string;
  /** Shown on the chip. Short — this is a status, not a sentence. */
  label: string;
  /** Spoken, and shown on hover. Says what the mode does. */
  hint?: string;
  /** Inline SVG, 14x14. */
  icon?: string;
  /** Invoked when the chip is dismissed. Omit to make the chip read-only. */
  onClear?: () => void;
  /**
   * Invoked when the chip itself is activated, making the whole thing a button.
   *
   * For the one case a status chip is also the fastest way out of the state it
   * reports: finishing a selection. The strip already floats over the canvas,
   * which is where the hand and the eye are — travelling back to the rail to
   * pick up the brush again is the cost this removes.
   */
  onSelect?: () => void;
}

export interface ModeChips {
  set(chip: ModeChip, on: boolean): void;
  clear(): void;
  destroy(): void;
}

const STYLE_ID = "mode-chips-styles";

const CSS = `
/*
  Anchored to the surface it describes rather than to the window, so it moves
  with a pane resize and cannot drift over an unrelated panel. pointer-events is
  set per element: the strip itself must not intercept a stroke that starts
  underneath it, only the chips may.
*/
.mc-strip {
  position: absolute; z-index: 30;
  inset-block-start: var(--space-sm, 12px); inset-inline-end: var(--space-sm, 12px);
  display: flex; flex-wrap: wrap; justify-content: flex-end;
  gap: var(--space-2xs, 6px);
  pointer-events: none;
  font-family: var(--type-family-ui, system-ui, sans-serif);
}
.mc-chip {
  display: inline-flex; align-items: center; gap: var(--space-3xs, 4px);
  min-height: 28px; padding: 0 var(--space-2xs, 6px) 0 var(--space-xs, 8px);
  border-radius: var(--radius-full, 999px);
  border: 1px solid light-dark(var(--azure-200, #b9d8f5), var(--azure-900, #1d3a55));
  background: light-dark(var(--azure-50, #eef6fd), var(--azure-975, #101d2b));
  color: light-dark(var(--azure-800, #12467c), var(--azure-200, #b9d8f5));
  font-size: var(--type-size-xs, 0.75rem); font-weight: var(--type-weight-medium, 500);
  letter-spacing: 0.01em;
  pointer-events: auto; cursor: pointer;
  box-shadow: var(--shadow-sm, 0 1px 3px rgb(0 0 0 / 0.18));
}
.mc-chip:hover { background: light-dark(var(--azure-100, #d7e9fa), var(--azure-950, #16293c)); }
.mc-chip:focus-visible {
  outline: var(--focus-width, 2px) solid var(--focus-ring, #2a7fd4);
  outline-offset: var(--focus-offset, 2px);
}
.mc-chip[data-readonly="true"] { cursor: default; padding-inline-end: var(--space-xs, 8px); }
.mc-x { opacity: 0.6; font-size: 13px; line-height: 1; }
.mc-chip:hover .mc-x { opacity: 1; }
.mc-icon { flex: none; }
`;

export function mountModeChips(host: HTMLElement, doc: Document = document): ModeChips {
  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  // The strip positions against the nearest positioned ancestor, and the pane
  // is not guaranteed to be one.
  if (getComputedStyle(host).position === "static") host.style.position = "relative";

  const strip = doc.createElement("div");
  strip.className = "mc-strip";
  // A status region, not an alert: it should be read when the user gets there,
  // never interrupt what they are doing.
  strip.setAttribute("role", "status");
  strip.setAttribute("aria-live", "polite");
  host.appendChild(strip);

  const shown = new Map<string, HTMLElement>();

  /** Put a chip's words on it. Shared by create and update, or they drift. */
  function fill(el: HTMLElement, chip: ModeChip) {
    el.title = chip.hint ?? chip.label;
    // The label alone is the accessible name; the × is decoration, and reading
    // "Symmetry times" helps nobody.
    el.setAttribute(
      "aria-label",
      chip.onSelect
        ? (chip.hint ?? chip.label)
        : chip.onClear
          ? `${chip.label} is on. Activate to turn it off.`
          : `${chip.label} is on`,
    );
    el.innerHTML = `${chip.icon ?? ""}<span>${chip.label}</span>${
      chip.onClear && !chip.onSelect ? '<span class="mc-x" aria-hidden="true">&times;</span>' : ""
    }`;
  }

  function set(chip: ModeChip, on: boolean) {
    const existing = shown.get(chip.id);
    if (!on) {
      existing?.remove();
      shown.delete(chip.id);
      return;
    }
    /*
      An existing chip is UPDATED, not left alone.

      It used to return early, on the reasoning that a mode is either on or off
      and a chip that is already up needs nothing. That is true of a mode with
      no detail and false of one that carries state: the face selection's label
      is the selection — "1 face", "Right arm" — so the chip sat there saying
      "1 face" while seven were selected, and looked exactly like a picker that
      had stopped responding.
    */
    if (existing) {
      fill(existing, chip);
      return;
    }

    const el = doc.createElement(chip.onClear || chip.onSelect ? "button" : "span");
    el.className = "mc-chip";
    if (el instanceof HTMLButtonElement) el.type = "button";
    else el.dataset.readonly = "true";
    fill(el, chip);
    if (chip.onSelect) el.addEventListener("click", () => chip.onSelect?.());
    else if (chip.onClear) el.addEventListener("click", () => chip.onClear?.());
    strip.appendChild(el);
    shown.set(chip.id, el);
  }

  return {
    set,
    clear() {
      for (const el of shown.values()) el.remove();
      shown.clear();
    },
    destroy() {
      strip.remove();
      shown.clear();
    },
  };
}
